import React, { useMemo, useState } from "react";
import { X, Download, Calendar, User, Mail, Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import LetterPreview from "./LetterPreview";
import html2pdf from "html2pdf.js";

export default function LetterViewModal({ letter, template, onClose }) {
  const companyProfile = useSelector((state) => state.letterHead.companyProfile);
  const [isDownloading, setIsDownloading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_ENTERPRISE_URL || "http://localhost:4040";

  const stripHtmlToText = (html) => {
    if (!html) return "";
    let text = html.replace(/<br\s*\/?>/gi, "\n");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const previewState = useMemo(() => {
    if (!letter) return null;
    const recipient = letter.letter_recipients?.[0] || {};
    const bodyMatch = letter.body?.match(/<div class="letter-content">([\s\S]*?)<\/div>/);
    const rawBody = bodyMatch ? bodyMatch[1] : letter.body;
    const cleanBody = stripHtmlToText(rawBody);

    const rawSettings = letter.signature_setting || letter.SignatureSetting || letter.signatureSetting;
    const sigSettings = Array.isArray(rawSettings) ? rawSettings[0] : (rawSettings || {});

    const rawSigs = letter.signatories || letter.LetterSignatories || letter.letter_signatories;
    const signatoriesData = Array.isArray(rawSigs) ? rawSigs : [];

    let signatureUrl = null;
    if (sigSettings) {
      if (Array.isArray(sigSettings.signature_files) && sigSettings.signature_files.length > 0) {
        signatureUrl = sigSettings.signature_files[0].file_path;
      } else if (sigSettings.signature_file?.file_path) {
        signatureUrl = sigSettings.signature_file.file_path;
      } else if (sigSettings.SignatureFile?.file_path) {
        signatureUrl = sigSettings.SignatureFile.file_path;
      }
    }

    if (!signatureUrl) {
      const sigMatch = letter.body?.match(/<div class="signature">.*?<img src="([^"]+)".*?<\/div>/);
      signatureUrl = sigMatch ? sigMatch[1].replace(/&amp;/g, '&') : null;
    }

    if (signatureUrl && signatureUrl.startsWith('/uploads')) {
      signatureUrl = `${BACKEND_URL}${signatureUrl}`;
    }

    let sigType = "text";

    if (sigSettings && sigSettings.signature_type) {
      sigType = sigSettings.signature_type;
    } else {
      if (signatoriesData && signatoriesData.length > 0) {
        sigType = "send_link";
      } else if (signatureUrl) {
        sigType = "upload";
      }
    }

    let qrSettings = {};
    const metaMatch = letter.body?.match(/data-qr="([^"]+)"/);
    if (metaMatch && metaMatch[1]) {
      try {
        const jsonStr = metaMatch[1].replace(/&quot;/g, '"');
        qrSettings = JSON.parse(jsonStr);
      } catch (e) { }
    }

    let savedTemplate = null;
    const templateMatch = letter.body?.match(/data-template="([^"]+)"/);
    if (templateMatch && templateMatch[1]) {
      try {
        let rawData = templateMatch[1];
        if (rawData.includes('&quot;')) {
          const cleanJson = rawData.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
          savedTemplate = JSON.parse(cleanJson);
        } else {
          savedTemplate = JSON.parse(decodeURIComponent(atob(rawData)));
        }
      } catch (e) {
        console.error("Failed to parse template snapshot", e);
      }
    }

    const sigPlacement = letter.signature_placement || {};

    return {
      recipientName: recipient.name || "",
      recipientEmail: recipient.email || "",
      recipientAddress: recipient.address || "",
      subject: letter.subject || "",
      body: cleanBody,
      created_at: (letter.status && letter.status !== "DRAFT")
        ? (letter.updated_at || letter.updatedAt || letter.created_at || letter.createdAt || letter.created_on)
        : (letter.created_at || letter.createdAt || letter.created_on),

      signatureType: sigType,
      uploadedSignatureUrl: sigType === 'upload' ? signatureUrl : null,
      drawnSignature: sigType === 'draw' ? signatureUrl : null,
      signaturePlacement: sigPlacement.type || 'last_page',
      signaturePagePoint: sigPlacement.page_from || 0,
      signatories: signatoriesData,

      esignVerified: sigSettings.verification_status === 'verified',
      esignSignerName: sigSettings.verification_signer_name || "",
      esignProvider: sigSettings.verification_provider || "DigiLocker Services",
      esignDate: sigSettings.updated_at
        ? new Date(sigSettings.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + " IST"
        : "",

      enableQR: qrSettings.enableQR,
      iqCode: qrSettings.iqCode,
      issuedBy: qrSettings.issuedBy,
      issuedTo: qrSettings.issuedTo,
      subjectQr: qrSettings.subject,
      savedTemplate: savedTemplate
    };
  }, [letter, BACKEND_URL]);

  const handleDownloadPDF = async () => {
    const elementToCapture = document.getElementById("letter-pages-only");
    if (!elementToCapture) return;

    setIsDownloading(true);

    try {
      const clone = elementToCapture.cloneNode(true);
      const uiElements = clone.querySelectorAll(".no-print, .sticky, h3, .modal-ui");
      uiElements.forEach(node => node.remove());

      const wrapper = clone.querySelector('.single-page-wrapper');
      if (wrapper) {
        wrapper.style.paddingBottom = "0px";
        wrapper.style.marginBottom = "0px";
      }

      const container = document.createElement("div");
      container.style.cssText = `
          position: fixed; 
          top: 0; 
          left: -9999px; 
          z-index: -9999; 
          width: 794px; 
          opacity: 0;
          pointer-events: none;
          background: white;
      `;
      container.appendChild(clone);
      document.body.appendChild(container);

      const svgs = clone.querySelectorAll('svg.MuiSvgIcon-root');
      svgs.forEach(svg => {
        svg.style.marginTop = "0px";
        svg.style.marginBottom = "0px";
        svg.style.transform = "translateY(2px)";
        svg.style.overflow = "visible";
        if (svg.parentElement) {
            svg.parentElement.style.overflow = "visible";
        }
      });

      const pages = clone.querySelectorAll(".single-page");
      pages.forEach((page, index) => {
        page.style.cssText = page.style.transform = "none";
        page.style.margin = "0px";
        page.style.marginBottom = "0px";
        page.style.width = "794px";
        page.style.height = "1122px";
        page.style.minWidth = "794px";
        page.style.minHeight = "1122px";
        page.style.boxShadow = "none";
        page.style.borderRadius = "0px";
        if (index < pages.length - 1) {
          page.style.pageBreakAfter = "always";
        } else {
          page.style.pageBreakAfter = "avoid";
        }
      });

      const images = clone.querySelectorAll("img");

      await Promise.all(
        Array.from(images).map(async (img) => {
          if (img.src && !img.src.startsWith("data:")) {
            try {
              const proxyUrl = `${BACKEND_URL}/api/letterHead/proxy-image?url=${encodeURIComponent(img.src)}`;
              const response = await fetch(proxyUrl);

              if (!response.ok) throw new Error("Proxy fetch failed");

              const data = await response.json();
              if (data.base64) {
                img.src = data.base64;
              }
            } catch (error) {
              console.warn("Failed to proxy image:", error);
              img.crossOrigin = "anonymous";
            }
          }

          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      );

      const options = {
        margin: 0,
        filename: `${previewState.recipientName || "letter"}.pdf`,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollY: 0,
          scrollX: 0,
          windowWidth: 794,
          windowHeight: 1123,
          letterRendering: true,
        },
        jsPDF: {
          unit: "px",
          format: [794, 1123],
          orientation: "portrait",
          compress: true
        },
        pagebreak: { mode: 'css' }
      };

      const target = clone.querySelector('.single-page-wrapper') || clone;
      await html2pdf().set(options).from(target).save();

      document.body.removeChild(container);
    } catch (error) {
      console.error("PDF Download Error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!letter || !previewState) return null;

  return (
    <div className="modal-backdrop position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-black bg-opacity-40 p-3 animate-in fade-in" style={{ zIndex: 100, backdropFilter: "blur(8px)" }}>
      <div className="bg-white w-100 rounded-3 shadow-2xl d-flex flex-column overflow-hidden border border-secondary border-opacity-25" style={{ maxWidth: "860px", height: "96vh" }}>

        <div className="flex-grow-1 overflow-hidden position-relative bg-light">
          <div id="letter-pages-only" className="w-100 overflow-auto position-absolute top-0 start-0 bottom-0 end-0" style={{ maxWidth: "1280px" }}>
            <LetterPreview
              state={previewState}
              selectedTemplate={previewState.savedTemplate || template}
              companyProfile={companyProfile}
            />
          </div>
        </div>

        <div className="modal-ui px-3 px-lg-5 py-3 bg-white border-top border-secondary border-opacity-25 flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3 text-secondary" style={{ fontSize: "0.75rem" }}>
              <span className="d-none d-sm-inline-flex align-items-center gap-2">
                <div className="rounded-circle bg-success" style={{ width: "6px", height: "6px" }}></div>
                Preview Mode
              </span>
              <span className="d-none d-md-inline">•</span>
              <span className="d-none d-md-inline">A4 Size</span>
            </div>

            <div className="d-flex gap-2 gap-md-3">
              <button
                onClick={onClose}
                disabled={isDownloading}
                className="btn btn-outline-secondary rounded-3 px-4 px-md-5 py-2 py-md-2 text-sm fw-medium transition-all duration-200 disabled-opacity-50"
                style={{ fontSize: "0.875rem" }}
              >
                Close
              </button>
              <button
                id="download-pdf-btn"
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                className="btn btn-dark rounded-3 px-4 px-md-5 py-2 py-md-2 text-sm fw-medium d-flex align-items-center gap-2 shadow-lg transition-all duration-200 active-scale-down disabled-opacity-70 disabled-cursor-wait"
                style={{ fontSize: "0.875rem" }}
              >
                {isDownloading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download size={18} strokeWidth={2} />
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}